import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useBrand } from "@/contexts/brand-context";
import { pb } from "@/lib/pocketbase";
import { cn } from "@/lib/utils";
import {
  ContentSchedules,
  ContentSchedulesStatusOptions,
} from "@/types/pocketbase-types";
import {
  faClock,
  faFileLines,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";

type ScheduledContent = {
  id: string;
  title: string;
  date: Date;
  time: string;
  status: ContentSchedulesStatusOptions;
  tags: string[];
  topic: string;
  keyword: string;
};

export default function ContentCalendar() {
  const [view, setView] = useState<"list" | "calendar" | "settings">("list");
  const [scheduledContent, setScheduledContent] = useState<ScheduledContent[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const { currentBrand } = useBrand();

  // Reset hasFetched when brand changes to trigger fresh fetch
  useEffect(() => {
    setHasFetched(false);
    setIsLoading(true);
  }, [currentBrand?.id]);

  // Fetch content schedules when brand is available and we haven't fetched yet
  useEffect(() => {
    const fetchContentSchedules = async () => {
      if (!currentBrand) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const records = await pb
          .collection("content_schedules")
          .getList<ContentSchedules>(1, 50, {
            filter: `brand = "${currentBrand?.id}" && status != "completed"`,
            sort: "scheduleDate",
            expand: "post",
          });

        const formattedSchedules = records.items.map(
          (record: ContentSchedules) => {
            // Parse the date string from the record
            const scheduleDate = new Date(record.scheduleDate);
            const formattedTime = scheduleDate.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "numeric",
              hour12: true,
            });

            // Get title from post if available, otherwise use topic
            const title = record.expand?.post?.title || `${record.topic}`;

            return {
              id: record.id,
              title: title,
              date: scheduleDate,
              time: formattedTime,
              tags: [record.keyword],
              status: record.status as ContentSchedulesStatusOptions,
              topic: record.topic,
              keyword: record.keyword,
              brand: record.brand,
              scheduleDate: record.scheduleDate,
              post: record.post,
              org: record.org,
            };
          },
        );

        setScheduledContent(formattedSchedules);
        setHasFetched(true);
      } catch (err: any) {
        console.error("Error fetching content schedules:", err);
        
        // Provide specific error messages based on the error type
        if (err?.status === 401) {
          setError("Authentication required. Please log in again.");
        } else if (err?.status === 403) {
          setError("You don't have permission to view this content.");
        } else if (err?.status === 404) {
          setError("Content schedules not found.");
        } else if (err?.message) {
          setError(err.message);
        } else {
          setError("Failed to load content schedules. Please try again later.");
        }
        
        setHasFetched(true);
      } finally {
        setIsLoading(false);
      }
    };

    // Only fetch if we have a brand and haven't fetched for this brand yet
    if (currentBrand && !hasFetched) {
      fetchContentSchedules();
    }
  }, [currentBrand, hasFetched]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold">Content Calendar</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {view === "list"
              ? "Your upcoming content publishing schedule"
              : "Your content calendar"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={view === "list" ? "default" : "outline"}
            size="sm"
            className="flex items-center gap-2"
            onClick={() => setView("list")}
          >
            <FontAwesomeIcon icon={faFileLines} className="h-5 w-5" />
            List View
          </Button>
          <Button
            variant={view === "calendar" ? "default" : "outline"}
            size="sm"
            className="flex items-center gap-2"
            onClick={() => setView("calendar")}
          >
            Calendar View
          </Button>
        </div>
      </div>

      {
        <Card variant="gray" className="mt-6">
          <CardHeader>
            <CardTitle>
              {view === "list" ? "Scheduled Content" : "Monthly Calendar"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <FontAwesomeIcon
                  icon={faSpinner}
                  spin
                  className="h-8 w-8 text-primary"
                />
                <span className="ml-3">Loading content schedules...</span>
              </div>
            ) : error ? (
              <div className="text-center py-8 text-destructive">
                <p>{error}</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </Button>
              </div>
            ) : scheduledContent.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No content scheduled yet.</p>
                {currentBrand && (
                  <p className="mt-2">
                    Content will be automatically scheduled based on your
                    subscription plan.
                  </p>
                )}
              </div>
            ) : view === "list" ? (
              <div className="space-y-4">
                {scheduledContent.map((content) => (
                  <Card
                    key={content.id}
                    className="hover:bg-accent/5 transition-colors"
                  >
                    <CardContent className="flex justify-between items-center p-4">
                      <div className="space-y-2">
                        <h3 className="font-medium">{content.title}</h3>
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-muted-foreground">
                            {content.date.toLocaleDateString("en-US", {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            })}{" "}
                            at {content.time}
                          </p>
                          <Badge
                            variant={
                              content.status === "scheduled"
                                ? "secondary"
                                : content.status === "in_progress"
                                  ? "default"
                                  : "outline"
                            }
                            className="text-xs"
                          >
                            {content.status === "scheduled"
                              ? "Scheduled"
                              : content.status === "in_progress"
                                ? "In Progress"
                                : content.status === "completed"
                                  ? "Completed"
                                  : "Failed"}
                          </Badge>
                          {content.tags.map((tag) => (
                            <Badge
                              key={tag}
                              variant="secondary"
                              className="text-xs"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-7 gap-4 text-sm font-medium text-center">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                    (day) => (
                      <div key={day} className="py-2">
                        {day}
                      </div>
                    ),
                  )}
                </div>
                <div className="grid grid-cols-7 gap-4">
                  {/* Get current month's days */}
                  {(() => {
                    const currentDate = new Date();
                    const year = currentDate.getFullYear();
                    const month = currentDate.getMonth();
                    const daysInMonth = new Date(year, month + 1, 0).getDate();
                    const firstDayOfMonth = new Date(year, month, 1).getDay();

                    // Create array for calendar days including empty cells for proper alignment
                    const calendarDays = [];

                    // Add empty cells for days before the 1st of the month
                    for (let i = 0; i < firstDayOfMonth; i++) {
                      calendarDays.push(null);
                    }

                    // Add days of the month
                    for (let i = 1; i <= daysInMonth; i++) {
                      calendarDays.push(i);
                    }

                    return calendarDays.map((day, index) => {
                      if (day === null) {
                        return (
                          <div
                            key={`empty-${index}`}
                            className="min-h-[100px] p-2 border rounded-lg opacity-50"
                          ></div>
                        );
                      }

                      const content = scheduledContent.find((c) => {
                        const contentDate = new Date(c.date);
                        return (
                          contentDate.getDate() === day &&
                          contentDate.getMonth() === month &&
                          contentDate.getFullYear() === year
                        );
                      });

                      return (
                        <div
                          key={`day-${day}`}
                          className={cn(
                            "min-h-[100px] p-2 border rounded-lg hover:bg-accent/5 transition-colors",
                            content ? "bg-accent/5" : "",
                          )}
                        >
                          <div className="font-medium text-center mb-2">
                            {day}
                          </div>
                          {content && (
                            <div className="mt-2 p-2 bg-background rounded border text-xs space-y-1">
                              <div className="font-medium line-clamp-2">
                                {content.title}
                              </div>
                              <div className="text-muted-foreground flex items-center gap-1">
                                <FontAwesomeIcon
                                  icon={faClock}
                                  className="h-3 w-3"
                                />
                                {content.time}
                              </div>
                              <Badge
                                variant={
                                  content.status === "scheduled"
                                    ? "secondary"
                                    : content.status === "in_progress"
                                      ? "default"
                                      : "outline"
                                }
                                className="text-xs mt-1 w-full flex justify-center"
                              >
                                {content.status === "scheduled"
                                  ? "Scheduled"
                                  : content.status === "in_progress"
                                    ? "In Progress"
                                    : content.status === "completed"
                                      ? "Completed"
                                      : "Failed"}
                              </Badge>
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      }
    </div>
  );
}
